import React, { useState } from 'react';
import { Pencil, Trash2, CheckSquare } from 'lucide-react';
import { format, parseISO, differenceInDays } from 'date-fns';

function getDaysLeft(expiryDateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = parseISO(expiryDateStr);
  return differenceInDays(expiry, today);
}

function getStatusBadge(daysLeft) {
  if (daysLeft < 0) return { label: `${Math.abs(daysLeft)}d overdue`, bg: '#fee2e2', color: '#dc2626', dot: '#dc2626' };
  if (daysLeft === 0) return { label: 'Expires TODAY', bg: '#fff7ed', color: '#ea580c', dot: '#ea580c' };
  if (daysLeft === 1) return { label: 'Expires TOMORROW', bg: '#fffbeb', color: '#d97706', dot: '#f59e0b' };
  if (daysLeft <= 7) return { label: `${daysLeft}d left`, bg: '#fefce8', color: '#a16207', dot: '#eab308' };
  if (daysLeft <= 30) return { label: `${daysLeft}d left`, bg: '#f0fdf4', color: '#15803d', dot: '#22c55e' };
  return { label: `${daysLeft}d left`, bg: '#f0f9ff', color: '#0369a1', dot: '#38bdf8' };
}

const PERIOD_ICONS = { monthly: '📅', quarterly: '📆', yearly: '🗓️' };
const PERIOD_LABELS = { monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly' };

export default function SubscriptionCard({ sub, onEdit, onDelete, onRenew }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [renewing, setRenewing] = useState(false);

  const daysLeft = getDaysLeft(sub.expiry_date);
  const isExpired = daysLeft < 0;
  const isDueToday = daysLeft === 0;
  const isDueSoon = daysLeft >= 0 && daysLeft <= 7;
  const status = getStatusBadge(daysLeft);

  const expiryDate = parseISO(sub.expiry_date);
  const formattedDate = format(expiryDate, 'd MMM yyyy');

  const handleRenew = async () => {
    setRenewing(true);
    try {
      await onRenew(sub.id);
    } finally {
      setRenewing(false);
    }
  };

  const cardStyle = {
    ...styles.card,
    borderLeft: `5px solid ${isExpired || isDueToday ? '#ef4444' : isDueSoon ? '#f59e0b' : sub.color}`,
    background: isExpired
      ? 'linear-gradient(135deg, #fff5f5, #fff)'
      : isDueToday
        ? 'linear-gradient(135deg, #fff7ed, #fff)'
        : 'white',
    boxShadow: isExpired
      ? '0 4px 20px rgba(239,68,68,0.15)'
      : '0 4px 16px rgba(0,0,0,0.06)',
  };

  return (
    <div style={cardStyle}>
      {/* Top row */}
      <div style={styles.topRow}>
        <div style={styles.colorDot(sub.color)} />
        <div style={styles.nameWrap}>
          <h3 style={styles.name}>{sub.name}</h3>
          {sub.notes && <p style={styles.notes}>{sub.notes}</p>}
        </div>
        <div style={styles.statusBadge(status)}>
          <span style={styles.statusDot(status.dot)} />
          {status.label}
        </div>
      </div>

      {/* Info row */}
      <div style={styles.infoRow}>
        <div style={styles.infoItem}>
          <span style={styles.infoIcon}>📅</span>
          <div>
            <div style={styles.infoLabel}>Expiry Date</div>
            <div style={{ ...styles.infoValue, color: isExpired ? '#dc2626' : '#1a1a2e', fontWeight: isExpired ? '700' : '600' }}>
              {formattedDate}
            </div>
          </div>
        </div>
        <div style={styles.infoItem}>
          <span style={styles.infoIcon}>{PERIOD_ICONS[sub.renewal_period]}</span>
          <div>
            <div style={styles.infoLabel}>Renewal</div>
            <div style={styles.infoValue}>{PERIOD_LABELS[sub.renewal_period]}</div>
          </div>
        </div>
      </div>

      {/* Action row */}
      <div style={styles.actionRow}>
        {(isExpired || isDueToday) && (
          <button
            onClick={handleRenew}
            disabled={renewing}
            style={styles.renewBtn}
          >
            {renewing ? (
              '⏳ Renewing...'
            ) : (
              <>
                <CheckSquare size={16} />
                Renewal Done
              </>
            )}
          </button>
        )}

        <div style={styles.iconActions}>
          <button onClick={() => onEdit(sub)} style={styles.iconBtn} title="Edit">
            <Pencil size={15} color="#6366f1" />
          </button>

          {confirmDelete ? (
            <div style={styles.confirmDelete}>
              <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: '600' }}>Delete?</span>
              <button onClick={() => onDelete(sub.id)} style={styles.confirmYes}>Yes</button>
              <button onClick={() => setConfirmDelete(false)} style={styles.confirmNo}>No</button>
            </div>
          ) : (
            <button onClick={() => setConfirmDelete(true)} style={styles.iconBtn} title="Delete">
              <Trash2 size={15} color="#ef4444" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  card: {
    borderRadius: '16px',
    padding: '20px',
    transition: 'transform 0.2s, box-shadow 0.2s',
    position: 'relative',
    overflow: 'hidden',
  },
  topRow: {
    display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '14px',
  },
  colorDot: (color) => ({
    width: '12px', height: '12px', borderRadius: '50%',
    background: color, flexShrink: 0, marginTop: '4px',
  }),
  nameWrap: { flex: 1 },
  name: {
    margin: 0, fontSize: '17px', fontWeight: '700', color: '#1a1a2e',
    lineHeight: '1.3',
  },
  notes: {
    margin: '3px 0 0', fontSize: '12px', color: '#888',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  statusBadge: (s) => ({
    display: 'flex', alignItems: 'center', gap: '5px',
    background: s.bg, color: s.color,
    padding: '4px 10px', borderRadius: '20px',
    fontSize: '12px', fontWeight: '700',
    whiteSpace: 'nowrap', flexShrink: 0,
  }),
  statusDot: (color) => ({
    width: '6px', height: '6px', borderRadius: '50%', background: color,
  }),
  infoRow: {
    display: 'flex', gap: '24px', marginBottom: '14px',
    padding: '12px 14px',
    background: 'rgba(0,0,0,0.025)', borderRadius: '10px',
  },
  infoItem: { display: 'flex', alignItems: 'center', gap: '8px' },
  infoIcon: { fontSize: '18px' },
  infoLabel: { fontSize: '11px', color: '#999', fontWeight: '500' },
  infoValue: { fontSize: '14px', color: '#1a1a2e', fontWeight: '600' },
  actionRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  renewBtn: {
    display: 'flex', alignItems: 'center', gap: '6px',
    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
    color: 'white', border: 'none',
    padding: '8px 16px', borderRadius: '10px',
    fontSize: '13px', fontWeight: '700',
    cursor: 'pointer', boxShadow: '0 3px 10px rgba(34,197,94,0.3)',
    transition: 'opacity 0.2s',
  },
  iconActions: { display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' },
  iconBtn: {
    background: '#f8f8f8', border: 'none',
    width: '34px', height: '34px', borderRadius: '10px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', transition: 'background 0.15s',
  },
  confirmDelete: {
    display: 'flex', alignItems: 'center', gap: '6px',
    background: '#fff5f5', padding: '4px 10px',
    borderRadius: '10px', border: '1px solid #fecaca',
  },
  confirmYes: {
    background: '#ef4444', color: 'white',
    border: 'none', padding: '3px 10px',
    borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer',
  },
  confirmNo: {
    background: '#e5e7eb', color: '#666',
    border: 'none', padding: '3px 10px',
    borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
  },
};
